// ═══════════════════════════════════════════════════════════
// SHARED PORT MAPPING UTILITIES
// Used by both SiteNetworkView and SiteRackView to build
// consistent port connection data from device_interfaces.
// ═══════════════════════════════════════════════════════════

export interface PortMapEntry {
  deviceType: string
  label?: string
  name?: string
  ip?: string
  offline: boolean
  color: string | null
  vlan?: number | string | null
  poe?: number
  speed?: string | null
}

export function mapCategoryToType(category?: string | null, type?: string | null): string {
  if (category === 'surveillance') return 'camera'
  if (category === 'access_control') return 'access'
  if (type === 'access_point') return 'networking'
  if (type === 'phone') return 'phone'
  if (type === 'switch') return 'uplink'
  return 'networking'
}

export function estimatePoE(type?: string | null): number {
  const estimates: Record<string, number> = { camera: 12, reader: 6, access_point: 18, phone: 5 }
  return estimates[type || ''] || 0
}

export function buildPortsMap(
  interfaces: Array<{
    port_number: number
    connected_device_id?: string | null
    vlan_id?: string | number | null
    poe_draw?: number | null
    poe_watts_draw?: number | null
    speed?: string | null
    status?: string | null
  }>,
  allDevices: Array<{
    id: string
    label?: string
    name?: string
    ip_address?: string
    status?: string
    category?: string
    type?: string
  }>
): Record<number, PortMapEntry> {
  const portsMap: Record<number, PortMapEntry> = {}
  const deviceMap = new Map(allDevices.map(d => [d.id, d]))

  for (const iface of interfaces) {
    if (!iface.connected_device_id) continue
    const connDevice = deviceMap.get(iface.connected_device_id)
    if (!connDevice) continue

    const deviceType = mapCategoryToType(connDevice.category, connDevice.type)
    portsMap[iface.port_number] = {
      deviceType,
      label: connDevice.label,
      name: connDevice.name,
      ip: connDevice.ip_address,
      offline: connDevice.status === 'offline' || connDevice.status === 'inactive',
      color: null,
      vlan: iface.vlan_id,
      poe: (iface.poe_draw ?? iface.poe_watts_draw ?? null) as number | undefined || estimatePoE(connDevice.type),
      speed: iface.speed,
    }
  }

  return portsMap
}

/**
 * Standardized device status color.
 * Applied consistently across all views.
 */
export function getStatusColor(status?: string | null): string {
  if (!status) return 'var(--text-3)'
  const s = status.toLowerCase()
  if (s === 'active' || s === 'online' || s === 'connected') return 'var(--ok)'
  if (s === 'offline' || s === 'inactive' || s === 'down') return 'var(--err)'
  if (s === 'degraded' || s === 'warning') return 'var(--warn)'
  return 'var(--text-3)'
}

/**
 * Check if a device status is considered "up/online"
 */
export function isStatusOnline(status?: string | null): boolean {
  if (!status) return false
  const s = status.toLowerCase()
  return s === 'active' || s === 'online' || s === 'connected'
}
